import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import {
  Bell,
  ClipboardList,
  FileText,
  Receipt,
  CreditCard,
  Users,
  ArrowRightLeft,
  HandCoins,
  ChevronRight,
} from 'lucide-react';

const ICON_MAP = {
  orders: ClipboardList,
  quotations: FileText,
  invoices: Receipt,
  credit_invoices: CreditCard,
  customers: Users,
  transfers: ArrowRightLeft,
  loans: HandCoins,
};

const COLOR_MAP_DARK = {
  orders: 'text-blue-400 bg-blue-500/15',
  quotations: 'text-amber-400 bg-amber-500/15',
  invoices: 'text-red-400 bg-red-500/15',
  credit_invoices: 'text-purple-400 bg-purple-500/15',
  customers: 'text-emerald-400 bg-emerald-500/15',
  transfers: 'text-indigo-400 bg-indigo-500/15',
  loans: 'text-orange-400 bg-orange-500/15',
};

const COLOR_MAP_LIGHT = {
  orders: 'text-blue-600 bg-blue-50',
  quotations: 'text-amber-600 bg-amber-50',
  invoices: 'text-red-600 bg-red-50',
  credit_invoices: 'text-purple-600 bg-purple-50',
  customers: 'text-emerald-600 bg-emerald-50',
  transfers: 'text-indigo-600 bg-indigo-50',
  loans: 'text-orange-600 bg-orange-50',
};

const REFRESH_INTERVAL = 60000; // 60 seconds

export default function NotificationBell({ isDark = true }) {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], total: 0 });
  const [lastSeenTotal, setLastSeenTotal] = useState(() => {
    return parseInt(localStorage.getItem('notif_last_seen_total') || '0', 10);
  });
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      // silent fail
    }
  }, [token]);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const badgeCount = Math.max(0, data.total - lastSeenTotal);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleMarkRead = () => {
    setLastSeenTotal(data.total);
    localStorage.setItem('notif_last_seen_total', String(data.total));
    setOpen(false);
  };

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-wrapper">
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-500'}`} style={isDark ? { filter: 'drop-shadow(0 0 4px #94a3b860)' } : {}} />
        {badgeCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse"
            data-testid="notification-badge"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className={`absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl z-[60] overflow-hidden ${isDark ? 'bg-slate-900/90 backdrop-blur-xl border border-white/10' : 'bg-white border border-slate-200'}`}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-100 bg-slate-50'}`}>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Notifications</h3>
            {data.total > 0 && (
              <button
                onClick={handleMarkRead}
                className="text-xs text-emerald-600 hover:text-emerald-500 font-medium"
                data-testid="mark-all-read-button"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <div className="px-4 py-8 text-center" data-testid="notification-empty">
                <Bell className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>You're all caught up</p>
              </div>
            ) : (
              data.notifications.map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                const colorMap = isDark ? COLOR_MAP_DARK : COLOR_MAP_LIGHT;
                const colorCls = colorMap[n.type] || (isDark ? 'text-slate-400 bg-slate-800/50' : 'text-slate-600 bg-slate-50');
                return (
                  <button
                    key={n.type}
                    onClick={() => handleNav(n.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left border-b last:border-b-0 ${isDark ? 'hover:bg-white/5 border-white/5' : 'hover:bg-slate-50 border-slate-50'}`}
                    data-testid={`notification-item-${n.type}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorCls}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{n.label}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {data.total > 0 && (
            <div className={`px-4 py-2.5 border-t ${isDark ? 'border-white/10' : 'border-slate-100 bg-slate-50'}`}>
              <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {data.total} item{data.total !== 1 ? 's' : ''} need attention
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
